import { showLoading, hideLoading } from 'react-redux-loading-bar';
import { wizardError, wizardAlert, wizardDone } from '../actions/wizard';
import { getSWVersion } from '../utils/index';
import { hasOptionalSetting } from './deviceTypes';
import compareVersions from 'compare-versions';

import {
  putConfiguration,
  postSettings,
  TimeoutError,
  requestReprovisionDevice,
} from './api';

const splitSettings = (
  values,
  releaseVersion = [],
  osVersion = '',
  isReprovision
) => {
  const {
    ShelfCount,
    CardTerminal,
    antenorTPVnr,
    wipayTerminalId,
    TimeZone,
    Country,
    City,
    Area,
    ...properties
  } = values;
  const settings = {};

  settings['timeZone'] = TimeZone.value;
  settings['country'] = Country.value;
  settings['city'] = City?.value;

  if (properties.ShelfConfig) {
    properties.ShelfConfig = undefined;
  }

  if (properties.ShelfIds) {
    properties.ShelfIds = undefined;
  }

  if (properties.SlotIds) {
    properties.SlotIds = undefined;
  }

  // add card payment as iotedge tag & cabinet setting
  if (
    hasOptionalSetting(properties.SubType, 'CARD_PAYMENT') &&
    CardTerminal &&
    CardTerminal !== '-'
  ) {
    const cardPaymentTag = { name: 'cardPayment', value: CardTerminal };
    settings['cardPayment'] = CardTerminal;
    settings['payment'] = CardTerminal;
    if (properties.Tags) {
      properties.Tags.push(cardPaymentTag);
    } else {
      properties.Tags = [cardPaymentTag];
    }

    if (
      CardTerminal === 'Axis-ingenico' ||
      CardTerminal === 'Axis-ingenico-Mews'
    ) {
      settings['antenorTPVnr'] = antenorTPVnr;
    } else if (CardTerminal === 'wipay') {
      settings['wipayTerminalId'] = wipayTerminalId;
      settings['wipayIncrementalAuth'] = 'true';
    } else if (CardTerminal === 'NoPayN') {
      // only need to update setting, not iotedge tags
      settings['disableRemainingAmountTransaction'] = 'true';
    } else if (CardTerminal === 'nayax-multisession') {
      properties.Tags.push({ name: 'isNayaxMultiSession', value: true });
      settings['nayaxMultiSession'] = 'true';
    }
  } else {
    settings['payment'] = 'demo';
  }

  if (!isReprovision) {
    // set area during provision
    if (Area.value) properties.Area = Area.value;
    else properties.Area = undefined;

    if (osVersion) {
      // last IF and DES operating system versions without host vpn
      const lastVersionWithoutHostVpnIF = '1.0.13';
      const lastVersionWithoutHostVpnDES = '1.0.2';
      // parse last version without host vpn based on device type
      // undefined is returned for devices that are not IF or DES
      const vpnHostOsLeastVersion =
        properties.Type === 'smartCabinet'
          ? lastVersionWithoutHostVpnIF
          : properties.Type === 'encodingStation'
          ? lastVersionWithoutHostVpnDES
          : undefined;

      let vpnHosted =
        compareVersions.validate(osVersion) &&
        compareVersions.validate(vpnHostOsLeastVersion) &&
        compareVersions(osVersion, vpnHostOsLeastVersion) >= 1
          ? true
          : false; // must be higher than 1.0.13 or 1.0.2 depending on deviceType
      const vpnTag = { name: 'vpn', value: { host: vpnHosted } };

      if (properties.Tags) {
        properties.Tags.push(vpnTag);
      } else {
        properties.Tags = [vpnTag];
      }
    }
  }

  const version =
    getSWVersion(
      releaseVersion,
      properties.Type,
      properties.SubType,
      osVersion
    ) || null;

  if (version) {
    const releaseTag = { name: 'release', value: version };
    if (properties.Tags) {
      properties.Tags.push(releaseTag);
    } else {
      properties.Tags = [releaseTag];
    }
  }

  // add DeviceSubType as tag if that exists and is not 'None'
  if (properties.SubType && properties.SubType !== 'None') {
    const subTypeTag = { name: 'subType', value: properties.SubType };
    if (properties.Tags) {
      properties.Tags.push(subTypeTag);
    } else {
      properties.Tags = [subTypeTag];
    }
  }

  // force certain reader profile for IF3 since there are currently no variants
  if (properties.SubType !== 'IF2') {
    settings['readerConfigId'] = 'if3-6-shelves';
  }

  if (hasOptionalSetting(properties.SubType, 'SHELF_COUNT')) {
    if (ShelfCount >= 4 && ShelfCount <= 8) {
      settings['readerConfigId'] = `${parseInt(ShelfCount, 10)}-shelves`;
    }
  }

  if (properties.Shelves) {
    let shelfConfigId = '';
    properties.Shelves.forEach(shelf => {
      if (shelfConfigId === '') {
        shelfConfigId = shelf.shelfId + '*' + shelf.slotId;
      } else {
        shelfConfigId =
          shelfConfigId + '+' + shelf.shelfId + '*' + shelf.slotId;
      }
    });

    let shelfCount = properties?.Shelves?.length || 2;

    switch (shelfCount) {
      case 2:
        settings['readerConfigId'] = 'if3-hyrbid-2-scale-shelves';
        break;
      case 3:
        settings['readerConfigId'] = 'if3-hyrbid-3-scale-shelves';
        break;
      case 4:
        settings['readerConfigId'] = 'if3-hyrbid-4-scale-shelves';
        break;
      case 5:
        settings['readerConfigId'] = 'if3-hyrbid-5-scale-shelves';
        break;
      case 6:
        settings['readerConfigId'] = 'if3-full-scale-no-rfid-shelves';
        break;
      default:
        // not included in the config, keep default readerConfigId settings
        break;
    }

    properties.Shelves = undefined;
    settings['shelfConfigId'] = shelfConfigId;
  }

  return { settings, properties };
};

// We get Tags as [{ name: 'tag1', value:'value1 },{ name: 'tag2', value:'value2 }, ...]
// Api consumes them as { tag1: 'value1', tag2: 'value2', ...}
// This spell does the transform
const collectTags = (fieldArray = []) =>
  Object.assign(
    {},
    ...fieldArray.map(({ name, value }) => ({ [name]: value }))
  );

const mapFields = ({ Id, Tags, Latitude, Longitude, ...other }) => ({
  Code: Id, // User given Id is actually stored in device's Code field and can be separate of the
  Tags: collectTags(Tags),
  Latitude: `${Latitude}`,
  Longitude: `${Longitude}`,
  ...other,
});

const sendConfiguration = (
  deviceId,
  deviceCode,
  releaseVersion,
  osVersion,
  isReprovision = false
) => (values, dispatch) => {
  dispatch(showLoading());
  const { properties, settings } = splitSettings(
    values,
    releaseVersion,
    osVersion,
    isReprovision
  );

  const requests = [];

  if (isReprovision) {
    const configuration = mapFields(properties);

    // Convert uppercase properties to lowercase until they can be changed to lowercase
    const data = {
      code: deviceCode,
      tags: configuration.Tags,
      type: configuration.Type,
      description: configuration.Description,
      name: configuration.Name,
      organization: configuration.Organization,
      reprovisionMode: configuration.ReprovisionMode,
      subType: configuration.SubType,
      latitude: configuration.Latitude,
      longitude: configuration.Longitude,
      settings,
    };

    requests.push(requestReprovisionDevice(data));
  } else {
    requests.push(putConfiguration(deviceId, mapFields(properties)));

    if (Object.keys(settings).length > 0) {
      requests.push(postSettings(properties.Code || deviceId, settings));
    }
  }

  return Promise.all(requests)
    .then(responses => {
      dispatch(hideLoading());
      if (responses.every(r => r.status === 200 || r.status === 201)) {
        const message = isReprovision
          ? 'messages.reprovision_success'
          : 'messages.provision_success';
        dispatch(wizardDone(message));
      } else if (responses.some(r => r.status === 403)) {
        dispatch(
          wizardError({
            title: 'errors.configure_forbidden',
            hint: 'errors.contact_administrators_hint',
          })
        );
      } else {
        dispatch(wizardAlert('errors.configure_failed_general'));
      }
    })
    .catch(error => {
      dispatch(hideLoading());
      if (error instanceof TimeoutError) {
        dispatch(wizardAlert('errors.configure_timeout'));
      } else {
        dispatch(wizardAlert('errors.configure_failed_general'));
      }

      console.error(error);
      throw error;
    });
};

export default sendConfiguration;
